import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import type { HoldList } from "api-client/models";
import { formatDateLong } from "@/utils/dateUtils";

interface ConfirmChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  selectedHolds: HoldList[];
  allHolds: HoldList[];
  onConfirm: (selectedHoldIds: string[]) => Promise<void>;
}

export const ConfirmChallengeDialog: React.FC<ConfirmChallengeDialogProps> = ({
  open,
  onClose,
  selectedHolds,
  allHolds,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Confirm/challenge actions require current pending holds; standby dates remain unavailable.
  const eligibleHolds = selectedHolds.flatMap((selected) => {
    const current = allHolds.find((hold) => hold.id === selected.id);
    return current?.status === 'pending' && (current.holdPosition || 0) > 0 ? [current] : [];
  });
  const canConfirm = selectedHolds.length > 0 && eligibleHolds.length === selectedHolds.length;
  const firstPositionHolds = eligibleHolds.filter((h) => h.holdPosition === 1);
  const higherPositionHolds = eligibleHolds.filter(
    (h) => (h.holdPosition || 0) > 1,
  );
  const unselectedHolds = allHolds.filter(
    (h) => !selectedHolds.find((sh) => sh.id === h.id),
  );

  const hasFirstPosition = firstPositionHolds.length > 0;
  const hasHigherPosition = higherPositionHolds.length > 0;
  const hasUnselectedHolds = unselectedHolds.length > 0;

  // Determine dialog mode
  const dialogMode =
    hasFirstPosition && !hasHigherPosition
      ? "confirm"
      : !hasFirstPosition && hasHigherPosition
        ? "challenge"
        : "mixed";

  const getTitle = () => {
    if (!canConfirm) return 'Review Selected Dates';
    switch (dialogMode) {
      case "confirm":
        return `Confirm ${firstPositionHolds.length} Date${firstPositionHolds.length > 1 ? "s" : ""}`;
      case "challenge":
        return `Challenge ${higherPositionHolds.length} Date${higherPositionHolds.length > 1 ? "s" : ""}`;
      case "mixed":
        return `Confirm & Challenge ${selectedHolds.length} Date${selectedHolds.length > 1 ? "s" : ""}`;
    }
  };

  const getPositionText = (position: number): string => {
    switch (position) {
      case 1:
        return "1st";
      case 2:
        return "2nd";
      case 3:
        return "3rd";
      case 4:
        return "4th";
      default:
        return `${position}th`;
    }
  };

  const handleConfirm = async () => {
    if (loading || !canConfirm) return;
    try {
      setLoading(true);
      setError(null);
      await onConfirm(selectedHolds.map((h) => h.id));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to process holds");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} disableEscapeKeyDown={loading} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {dialogMode === "confirm" && <CheckCircleIcon color="primary" />}
          {dialogMode === "challenge" && <WarningIcon color="warning" />}
          {dialogMode === "mixed" && <InfoIcon color="info" />}
          <Typography variant="h6">{getTitle()}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {!canConfirm && (
            <Alert severity="warning">
              Only current pending holds can be confirmed or challenged. Standby holds remain unavailable while the date is confirmed.
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 1st Position Auto-Confirm Section */}
          {firstPositionHolds.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <CheckCircleIcon
                  sx={{ fontSize: 18, verticalAlign: "middle", mr: 1 }}
                  color="success"
                />
                Auto-Confirm ({firstPositionHolds.length})
              </Typography>
              <Alert severity="success" sx={{ mb: 1 }}>
                These dates are in 1st position and will be confirmed
                immediately. Competing holds from other shows on the same stage and date will be released.
                Other dates of this show are not automatically released.
              </Alert>
              <List dense>
                {firstPositionHolds.map((hold) => (
                  <ListItem
                    key={hold.id}
                    sx={{ bgcolor: "success.light", mb: 0.5, borderRadius: 1, flexWrap: "wrap", gap: 1 }}
                  >
                    <ListItemText
                      primary={formatDateLong(hold.holdDate)}
                      secondary={`${hold.stageName} at ${hold.stageVenueName}`}
                    />
                    <Chip
                      label="1st position"
                      color="success"
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Higher Position Challenge Section */}
          {higherPositionHolds.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <InfoIcon
                  sx={{ fontSize: 18, verticalAlign: "middle", mr: 1 }}
                  color="info"
                />
                Challenge Cascade ({higherPositionHolds.length})
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                These dates will initiate a{" "}
                <strong>top-down challenge cascade</strong>. The 1st position
                hold will receive a 24-hour notification to confirm or release.
                If they release, the challenge cascades to the next position,
                and so on until someone confirms or you reach 1st position.
              </Alert>
              <List dense>
                {higherPositionHolds.map((hold) => (
                  <ListItem
                    key={hold.id}
                    sx={{ bgcolor: "info.light", mb: 0.5, borderRadius: 1, flexWrap: "wrap", gap: 1 }}
                  >
                    <ListItemText
                      primary={formatDateLong(hold.holdDate)}
                      secondary={`${hold.stageName} at ${hold.stageVenueName} • Challenges 1st position (top-down cascade)`}
                      primaryTypographyProps={{
                        sx: { color: "text.primary", fontWeight: 500 },
                      }}
                      secondaryTypographyProps={{
                        sx: { color: "text.secondary" },
                      }}
                    />
                    <Chip
                      label={`${getPositionText(hold.holdPosition || 0)} position`}
                      color="info"
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Unselected Holds Notice */}
          {hasUnselectedHolds && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  <InfoIcon
                    sx={{ fontSize: 18, verticalAlign: "middle", mr: 1 }}
                    color="info"
                  />
                  Unselected Dates Retained ({unselectedHolds.length})
                </Typography>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Unselected dates are not automatically released. After a date is confirmed,
                  pending sibling dates keep their 24-hour decision window. Already-confirmed
                  dates remain confirmed.
                </Alert>
                <List dense>
                  {unselectedHolds.map((hold) => (
                    <ListItem
                      key={hold.id}
                      sx={{
                        flexWrap: "wrap", gap: 1,
                        bgcolor: "info.light",
                        mb: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemText
                        primary={formatDateLong(hold.holdDate)}
                        secondary={`${hold.stageName} at ${hold.stageVenueName} • ${getPositionText(hold.holdPosition || 0)} position`}
                        primaryTypographyProps={{
                          sx: { color: "text.primary", fontWeight: 500 },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: "text.secondary" },
                        }}
                      />
                      <Chip
                        label="Retained"
                        color="info"
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {/* Summary */}
          <Divider />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Summary
            </Typography>
            <Stack spacing={1}>
              {firstPositionHolds.length > 0 && (
                <Typography variant="body2">
                  ✅{" "}
                  <strong>
                    {firstPositionHolds.length} date
                    {firstPositionHolds.length > 1 ? "s" : ""}
                  </strong>{" "}
                  will be auto-confirmed
                </Typography>
              )}
              {higherPositionHolds.length > 0 && (
                <Typography variant="body2">
                  ⚠️{" "}
                  <strong>
                    {higherPositionHolds.length} date
                    {higherPositionHolds.length > 1 ? "s" : ""}
                  </strong>{" "}
                  will initiate challenge cascade
                </Typography>
              )}
              {hasUnselectedHolds && (
                <Typography variant="body2" color="statusText.settled">
                  ℹ️{" "}
                  <strong>
                    {unselectedHolds.length} date
                    {unselectedHolds.length > 1 ? "s" : ""}
                  </strong>{" "}
                  will be retained
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} autoFocus>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={dialogMode === "confirm" ? "primary" : "warning"}
          disabled={loading || !canConfirm}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {!canConfirm
            ? "Unavailable"
            : loading
            ? "Processing..."
            : dialogMode === "confirm"
              ? `Confirm ${selectedHolds.length} Date${selectedHolds.length > 1 ? "s" : ""}`
              : dialogMode === "challenge"
                ? `Start Challenge for ${selectedHolds.length} Date${selectedHolds.length > 1 ? "s" : ""}`
                : `Confirm & Challenge ${selectedHolds.length} Date${selectedHolds.length > 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
